import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "@app/store";
import {
  createProduct,
  updateProduct,
  fetchProductById,
  clearError,
  clearValidations,
  clearSelectedProduct,
} from "./productSlice";
import { IProduct, IProductPhase, IActivity, ActivityType } from "./types";
import { ApiError } from "@/types/api";
import { productMapper } from "./productMapper";
import { useNotification } from "@hooks/useNotification";

// Import from phaseForm
import { fetchPhaseForms } from "@features/phaseForm/phaseFormSlice";

// UI Components
import { Card, CardContent } from "@components/ui/card";
import axios from "axios";
import { API_URL, getAuthHeaders } from "@/config/api";
import { fetchDocuments } from "../document";
import { fetchTemplates } from "../template";
import { fetchCategoryDocuments } from "../categorydocument";
import { fetchTechinicals } from "../techinicaldocument/TechinicalSlice";

//* Components imports
import { ProductSection } from "../Element/sections/ProductSection";
import { ProductFormMenu } from "./sections/ProductFormMenu";
import { PhaseSection } from "./sections/PhaseSection";

// Estendendo a interface IProduct para incluir a propriedade description
export interface IProductExtended extends IProduct {
  description?: string;
}

const ProductFormNew: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedProduct: currentItem, loading } = useSelector(
    (state: RootState) => state.product,
  );
  const { items: availableForms } = useSelector(
    (state: RootState) => state.phaseForm,
  );

  const {
    items: documents,
    loading: loading_document,
    error: error_document,
  } = useSelector((state: RootState) => {
    // console.log('🔍 Selector executando, loading:', state.document.loading);
    return state.document;
  });

  const {
    items: templates,
    loading: loading_template,
    error: error_template,
  } = useSelector((state: RootState) => {
    // console.log('🔍 Selector executando, loading:', state.template.loading);
    return state.template;
  });

  const {
    items: categories,
    loading: loading_categories,
    error: error_categories,
  } = useSelector((state: RootState) => {
    // console.log('🔍 Selector executando, loading:', state.categorydocument.loading);
    return state.categorydocument;
  });

  const {
    items: techinicals,
    loading: loading_techinical,
    error: error_techinical,
  } = useSelector((state: RootState) => {
    return state.techinical;
  });

  const [formData, setFormData] = useState<Partial<IProductExtended>>({
    tutorial: {},
  });
  const [localPhases, setLocalPhases] = useState<IProductPhase[]>([]);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadingFile, setLoadingFile] = useState(false);
  const [tabSelect, setTabSelect] = useState(0);

  const notification = useNotification();
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);
  const [appointments, setAppointments] = useState([]);
  const [selectType, setSelectType] = useState("operacional");
  // Effect for initialization and unmount cleanup
  useEffect(() => {
    // Fetch available forms on mount
    dispatch(fetchPhaseForms());

    // If it's a new product form, ensure states are reset and not loading.
    if (!id) {
      setIsInitialLoad(false);
      setFormData({ tutorial: { type: "text" } });
      setLocalPhases([]);
      initialLoadDoneRef.current = true; // Mark initial setup as done for the "new" case
    } else {
      // If we are in edit mode, reset the flag to allow data fetching for the new id.
      initialLoadDoneRef.current = false;
    }

    // Cleanup when component unmounts
    return () => {
      console.log("🧹 Cleaning form state on unmount");
      dispatch(clearError());
      dispatch(clearValidations());
      dispatch(clearSelectedProduct());
    };
  }, [id, dispatch]);

  const loading_docs = useRef(true);

  const loadDocumentos = async () => {
    loading_docs.current = true;

    await dispatch(fetchDocuments({}));
    await dispatch(fetchCategoryDocuments({}));

    await dispatch(fetchTemplates({}));
    await dispatch(fetchTechinicals({}));
    loading_docs.current = false;
  };
  useEffect(() => {
    loadDocumentos();
  }, []);

  // Effect to load data only when editing
  useEffect(() => {
    if (!id) {
      return; // Don't load data if it's a new product
    }

    const loadData = async () => {
      // Prevent re-fetching on subsequent renders
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Starting product load for editing:", id);
        isLoadingRef.current = true;
        setIsInitialLoad(true); // Set loading state for edit mode

        const resultAction = await dispatch(fetchProductById(id));

        if (fetchProductById.fulfilled.match(resultAction)) {
          console.log("✅ Product loaded successfully");
        } else {
          throw new Error("Error loading data");
        }
      } catch (error) {
        console.error("❌ Error loading Product:", error);
        notification.showError("Error loading data");
        setFormError({
          message: "Error loading data",
          error: "Error loading data",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();
  }, [id, dispatch, notification]);
  const first: any = useRef();
  // Effect to update form data when currentItem changes
  useEffect(() => {
    if (currentItem && first.current != false) {
      if (loading_docs.current == false) {
        first.current = false;
      }
      const adaptedItem: IProductExtended = {
        ...currentItem,
        financialConfig: currentItem.financialConfig,
        description: currentItem.description || "",
        phases: currentItem.phases.map((phase: IProductPhase) => ({
          ...phase,
          type: phase.type ? phase.type : "operacional",
          activities: phase.activities.map((act: IActivity) => {
            let activity: any = { ...act };
            activity.days = Math.floor(activity.duration / (24 * 60)) || 0;
            activity.hours =
              Math.floor((activity.duration % (24 * 60)) / 60) || 0;

            if (activity.documents == undefined) {
              activity.documents = [];
            }

            if (activity.templates == undefined) {
              activity.templates = [];
            }

            if (activity.techinicals == undefined) {
              activity.techinicals = [];
            }

            let docs = activity.documents
              ?.map((item2: any) => {
                let doc = documents.filter((item3) => item2 == item3._id)[0];
                if (doc == undefined) {
                  return undefined;
                }
                return doc;
              })
              .filter((item2: any) => item2 != undefined);

            let docs_oficial: any = {};
            for (let i = 0; i < docs.length; i++) {
              docs_oficial[docs[i]._id] = docs[i];
            }

            let temps = activity.templates
              ?.map((item2: any) => {
                let doc = templates.filter((item3) => item2 == item3._id)[0];
                if (doc == undefined) {
                  return undefined;
                }
                return doc;
              })
              .filter((item2: any) => item2 != undefined);

            let temps_oficial: any = {};
            for (let i = 0; i < temps.length; i++) {
              temps_oficial[temps[i]._id] = temps[i];
            }

            let techs = activity.techinicals
              ?.map((item2: any) => {
                let doc = techinicals.filter((item3) => item2 == item3._id)[0];
                if (doc == undefined) {
                  return undefined;
                }
                return doc;
              })
              .filter((item2: any) => item2 != undefined);

            let techs_oficial: any = {};
            for (let i = 0; i < techs.length; i++) {
              techs_oficial[techs[i]._id] = techs[i];
            }

            return {
              ...activity,
              documents: docs_oficial,
              templates: temps_oficial,
              techinicals: techs_oficial,

              type: activity.type as ActivityType,
            };
          }),
          subprocessGenerators: phase.subprocessGenerators || [],
        })),
      };

      setFormData(adaptedItem);
      setLocalPhases(
        adaptedItem.phases.sort((a, b) => (a.order || 0) - (b.order || 0)),
      );
    } else {
      setFormData({ ...formData });
    }
  }, [currentItem, documents, templates, techinicals]);

  // Handler to submit the form
  const handleSubmit = async () => {
    console.log("submit");
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, por favor aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setFormError(null);

      // Client-side validation
      if (!formData.name) {
        const error: ApiError = {
          message: "Por favor, corrija os erros de validação.",
          code: "VALIDATION_ERROR",
          statusCode: 400,
          validationErrors: {
            name: {
              message: "O nome do produto é obrigatório.",
              code: "REQUIRED_FIELD",
            },
          },
        };
        setFormError(error);
        notification.showError(error.message);
        setIsSubmitting(false);
        submitAttemptRef.current = false;
        return;
      }
      if (!formData.number_product) {
        const error: ApiError = {
          message: "Por favor, corrija os erros de validação.",
          code: "VALIDATION_ERROR",
          statusCode: 400,
          validationErrors: {
            number_product: {
              message: "O número do produto é obrigatório.",
              code: "REQUIRED_FIELD",
            },
          },
        };
        setFormError(error);
        notification.showError(error.message);
        setIsSubmitting(false);
        submitAttemptRef.current = false;
        return;
      }

      const validationErrors: any = {};
      let hasFinancialErrors = false;

      if (!formData.financialConfig) {
        validationErrors.financialConfig = {
          message: "A configuração financeira é obrigatória.",
          code: "REQUIRED_FIELD",
        };
        hasFinancialErrors = true;
      } else {
        if (!formData.financialConfig.cashPrice) {
          validationErrors.cashPrice = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (!formData.financialConfig.installmentPrice) {
          validationErrors.installmentPrice = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (
          !formData.financialConfig.paymentMethods ||
          formData.financialConfig.paymentMethods.length === 0
        ) {
          validationErrors.paymentMethods = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (!formData.financialConfig.maxInstallments) {
          validationErrors.maxInstallments = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (formData.financialConfig.interestFreeInstallments === undefined) {
          validationErrors.interestFreeInstallments = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (formData.financialConfig.interestRate === undefined) {
          validationErrors.interestRate = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (formData.financialConfig.standardCashDiscount === undefined) {
          validationErrors.standardCashDiscount = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (
          formData.financialConfig.standardInstallmentDiscount === undefined
        ) {
          validationErrors.standardInstallmentDiscount = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
        if (formData.financialConfig.managerOverrideDiscount === undefined) {
          validationErrors.managerOverrideDiscount = {
            message: "Obrigatório",
            code: "REQUIRED_FIELD",
          };
          hasFinancialErrors = true;
        }
      }

      if (hasFinancialErrors) {
        const error: ApiError = {
          message:
            "Por favor, preencha todos os campos da configuração financeira.",
          code: "VALIDATION_ERROR",
          statusCode: 400,
          validationErrors: {
            ...validationErrors,
            financialConfig: {
              message:
                "A configuração financeira deve ser preenchida completamente.",
              code: "REQUIRED_FIELD",
            },
          },
        };
        setFormError(error);
        notification.showError(error.message);
        setIsSubmitting(false);
        submitAttemptRef.current = false;
        return;
      }

      for (let i = 0; i < localPhases.length; i++) {
        if (!localPhases[i].name) {
          const error: ApiError = {
            message: "Por favor, corrija os erros de validação.",
            code: "VALIDATION_ERROR",
            statusCode: 400,
            validationErrors: {
              phases: {
                message: "O Título da fase " + (i + 1) + " é obrigatório.",
                code: "REQUIRED_FIELD",
              },
            },
          };
          setFormError(error);
          notification.showError(error.message);
          setIsSubmitting(false);
          submitAttemptRef.current = false;
          return;
        }
        for (let j = 0; j < localPhases[i].activities.length; j++) {
          if (!localPhases[i].activities[j].name) {
            const error: ApiError = {
              message: "Por favor, corrija os erros de validação.",
              code: "VALIDATION_ERROR",
              statusCode: 400,
              validationErrors: {
                activities: {
                  message:
                    "O Título da ativadade " +
                    (j + 1) +
                    " da fase " +
                    (i + 1) +
                    " é obrigatório.",
                  code: "REQUIRED_FIELD",
                },
              },
            };
            setFormError(error);
            notification.showError(error.message);
            setIsSubmitting(false);
            submitAttemptRef.current = false;
            return;
          }
          console.log(
            "dias",
            localPhases[i].activities[j].days,
            "horas",
            localPhases[i].activities[j].hours,
          );
          localPhases[i].activities[j].duration =
            localPhases[i].activities[j].days * 24 * 60 +
            localPhases[i].activities[j].hours * 60;
          if (
            !localPhases[i].activities[j].duration ||
            isNaN(localPhases[i].activities[j].duration)
          ) {
            const error: ApiError = {
              message: "Por favor, corrija os erros de validação.",
              code: "VALIDATION_ERROR",
              statusCode: 400,
              validationErrors: {
                activities: {
                  message:
                    "O campo Dias e Horas da ativadade " +
                    (j + 1) +
                    " da fase " +
                    (i + 1) +
                    " é obrigatório.",
                  code: "REQUIRED_FIELD",
                },
              },
            };
            setFormError(error);
            notification.showError(error.message);
            setIsSubmitting(false);
            submitAttemptRef.current = false;
            return;
          }
        }
      }

      // Add phases to the payload
      const finalData = {
        name: formData.name,
        number_product: formData.number_product,
        image: formData.image,
        description: formData.description,
        phases: localPhases.map((phase, index) => ({
          ...phase,
          activities: phase.activities.map((item2: any) => {
            let item3 = { ...item2 };
            console.log("atividade", { ...item3 });
            item3.documents = Object.keys(item3.documents);
            item3.templates = Object.keys(item3.templates);
            if (item3.techinicals) {
              item3.techinicals = Object.keys(item3.techinicals);
            }
            console.log("atividade2", { ...item3 });

            return item3;
          }),
          order: index,
          subprocessGenerators: phase.subprocessGenerators || [],
        })),
        matters: formData.matters || [],
        tutorial: formData.tutorial,
        financialConfig: formData.financialConfig,
      };

      setIsSubmitting(true);

      console.log("this", finalData);
      const mappedData = productMapper.toDTO(finalData as IProduct);
      console.log("📤 Sending data:", mappedData);
      // setIsSubmitting(false);
      // return;

      const resultAction = id
        ? await dispatch(updateProduct({ id, product: mappedData }))
        : await dispatch(createProduct(mappedData));

      if (
        updateProduct.rejected.match(resultAction) ||
        createProduct.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/products");
    } catch (error) {
      console.error("❌ ProductForm - Unexpected error:", error);
      notification.showError("Erro inesperado ao salvar dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        error: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Handler to add a new phase
  const handleAddPhase = () => {
    const newPhase: IProductPhase = {
      name: `Fase ${localPhases.length + 1}`,
      description: "Descrição da fase",
      type: selectType,
      order: localPhases.length,
      activities: [],
      subprocessGenerators: [],
      tutorial: { type: "text", link: "" },
    };
    setLocalPhases([...localPhases, newPhase]);
  };

  // Handler to update a phase
  const handlePhaseUpdate = (index: number, updatedPhase: IProductPhase) => {
    const newPhases = [...localPhases];
    newPhases[index] = updatedPhase;
    setLocalPhases(newPhases);
  };

  // Handler to remove a phase
  const handlePhaseRemove = (index: number) => {
    const newPhases = [...localPhases];
    newPhases.splice(index, 1);

    // Update order for all phases after removal
    const reorderedPhases = newPhases.map((phase, idx) => ({
      ...phase,
      order: idx,
    }));

    setLocalPhases(reorderedPhases);
  };

  const saveFile = async (file: any) => {
    let headers = getAuthHeaders();
    delete headers["Content-Type"];
    // headers['Content-Type'] = 'multipart/form-data';
    let form_data = new FormData();
    form_data.append("file", file);
    form_data.append("mime", file.type.split("/")[1]);

    let response = await axios.post(API_URL + "/products/file", form_data, {
      headers,
    });
    console.log(response);
    return response;
  };

  // Handler to add a new activity
  const handleAddActivity = (phaseIndex: number) => {
    const newActivity: IActivity = {
      name: `Atividade ${localPhases[phaseIndex].activities.length + 1}`,
      description: "Descrição da atividade",
      order: localPhases[phaseIndex].activities.length,
      duration: 0,
      hours: "",
      days: "",
      type: ActivityType.FORM,
      typeRefId: "",
      tutorial: { type: "text", link: "" },
      documents: {},
      templates: {},
    };
    const updatedPhases = [...localPhases];
    updatedPhases[phaseIndex].activities = [
      ...updatedPhases[phaseIndex].activities,
      newActivity,
    ];
    setLocalPhases(updatedPhases);
  };

  // Handler to update an activity
  const handleActivityUpdate = (
    phaseIndex: number,
    activityIndex: number,
    updatedActivity: IActivity,
  ) => {
    const updatedPhases = [...localPhases];
    updatedPhases[phaseIndex].activities[activityIndex] = updatedActivity;
    setLocalPhases(updatedPhases);
  };

  // Handler to remove an activity
  const handleActivityRemove = (phaseIndex: number, activityIndex: number) => {
    const updatedPhases = [...localPhases];
    updatedPhases[phaseIndex].activities.splice(activityIndex, 1);

    // Update order for all activities after removal
    updatedPhases[phaseIndex].activities = updatedPhases[
      phaseIndex
    ].activities.map((activity, idx) => ({
      ...activity,
      order: idx,
    }));

    setLocalPhases(updatedPhases);
  };

  const handleFileChange = async (e: any) => {
    if (e.target.files[0] != undefined) {
      console.log(e.target.name, e.target.files[0]);
      setLoadingFile(true);
      let response = await saveFile(e.target.files[0]);
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: response?.data?.path,
      }));
      setLoadingFile(false);
    }
  };

  if (isInitialLoad && loading) {
    return (
      <div className="flex flex-1 justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-1 lg:gap-6 md:gap-0 p-6 bg-gray-50 min-h-screen"
      style={{ flexWrap: "wrap" }}
    >
      <div className="flex flex-col flex-1 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">
            {id ? "Editar Produto" : "Novo Produto"}
          </h1>
          {/* <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/products")}
              variant="outline"
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </div> */}
        </div>

        {/* Main Content */}
        <Card
          style={{ height: window.innerHeight - 100, overflow: "scroll" }}
          id="scroll-box"
          className="custom-scrollbar w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors"
        >
          <CardContent className="p-6">
            <div className="flex flex-col gap-8">
              <ProductSection
                handleFileChange={handleFileChange}
                formData={formData}
                loadingFile={loadingFile}
                setFormData={setFormData}
                error={formError}
                saveFile={saveFile}
              />
              {/* <Separator className="my-8" /> */}
              <div
                style={{
                  display: "flex",
                  marginTop: 15,
                  marginBottom: 15,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  onClick={(_e) => {
                    setSelectType("operacional");
                  }}
                  style={{
                    width: 150,
                    maxWidth: "50%",
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #f2f2f2",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    background:
                      selectType == "operacional" ? "#1777CF" : "white",
                    color: selectType == "operacional" ? "white" : "#1777CF",
                  }}
                >
                  Operacional
                </div>
                <div
                  onClick={(_e) => {
                    setSelectType("comercial");
                  }}
                  style={{
                    width: 150,
                    maxWidth: "50%",
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #f2f2f2",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    background: selectType == "comercial" ? "#1777CF" : "white",
                    color: selectType == "comercial" ? "white" : "#1777CF",
                  }}
                >
                  Comercial
                </div>
              </div>
              <div
                style={{
                  padding: 10,
                  backgroundColor: "#f2f2f2",
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    padding: 10,
                    backgroundColor: "white",
                    borderRadius: 10,
                  }}
                >
                  <PhaseSection
                    phases={localPhases}
                    formData={formData}
                    selectType={selectType}
                    saveFile={saveFile}
                    documents={documents}
                    categories={categories}
                    templates={templates}
                    techinicals={techinicals}
                    loadDocumentos={loadDocumentos}
                    onPhaseUpdate={handlePhaseUpdate}
                    onPhaseRemove={handlePhaseRemove}
                    onAddPhase={handleAddPhase}
                    error={formError}
                    onActivityUpdate={handleActivityUpdate}
                    onActivityRemove={handleActivityRemove}
                    onAddActivity={handleAddActivity}
                    availableForms={availableForms}
                    setTabSelect={setTabSelect}
                    tabSelect={tabSelect}
                    setAppointments={setAppointments}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Side Menu */}
      <div className="sm:w-full lg:w-80  mt-14">
        <ProductFormMenu
          formData={formData}
          tabSelect={tabSelect}
          setTabSelect={setTabSelect}
          appointments={appointments}
          phases={localPhases}
          onSave={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default ProductFormNew;
